import type { VilniusPublicTransportMode } from "../world/busStops.ts";

export interface PublicTransportVehicleAssetSpec {
  readonly mode: VilniusPublicTransportMode;
  readonly assetId: string;
  readonly assetUrl: string;
  readonly lod1Url: string;
  readonly dimensionsM: Readonly<{ x: number; y: number; z: number }>;
  readonly lodDistanceM: number;
}

export const PUBLIC_TRANSPORT_VEHICLE_ASSETS: Readonly<
  Record<VilniusPublicTransportMode, PublicTransportVehicleAssetSpec>
> = Object.freeze({
  bus: Object.freeze({
    mode: "bus" as const,
    assetId: "vilnius-city-bus",
    assetUrl: "/assets/vehicles/vilnius-city-bus.glb",
    lod1Url: "/assets/vehicles/vilnius-city-bus-lod1.glb",
    dimensionsM: Object.freeze({ x: 2.55, y: 3.25, z: 12 }),
    lodDistanceM: 180,
  }),
  trolleybus: Object.freeze({
    mode: "trolleybus" as const,
    assetId: "vilnius-new-trolleybus",
    assetUrl: "/assets/vehicles/vilnius-new-trolleybus.glb",
    lod1Url: "/assets/vehicles/vilnius-new-trolleybus-lod1.glb",
    dimensionsM: Object.freeze({ x: 2.55, y: 3.45, z: 12 }),
    lodDistanceM: 190,
  }),
});

/**
 * PROVISIONAL — USER TUNING REQUIRED.
 *
 * Route geometry, stops and base schedule come from JUDU. These values expose
 * the gameplay-facing dwell, activation and passenger presentation choices.
 */
export const PUBLIC_TRANSPORT_TUNING = Object.freeze({
  initialServiceTimeSeconds: 12 * 60 * 60,
  serviceDaySeconds: 24 * 60 * 60,
  minimumStopDwellSeconds: 8,
  terminalHoldSeconds: 30,
  boardingRadiusM: 3.4,
  maximumBoardingHeightDifferenceM: 2.2,
  exitClearanceBeyondBodyM: 0.55,
  exitCandidateStepM: 0.8,
  exitCandidateCount: 3,
  routeVehicleGroundLiftM: 0.055,
  passengerLocalPosition: Object.freeze({ x: 0.62, y: 1.08, z: 0.2 }),
  passengerCameraTargetHeightM: 2.25,
  passengerCameraDistanceM: 20,
  renderRadiusM: 1_050,
  maximumRenderedVehicles: 28,
  colliderRadiusM: 180,
  maximumColliderVehicles: 10,
  routeDisplayWidthM: 2.0,
  routeDisplayHeightM: 0.42,
  routeDisplaySurfaceOffsetM: 0.025,
  maximumHeadsignCharacters: 24,
});

export const PUBLIC_TRANSPORT_REVIEW_ROUTES = Object.freeze({
  bus: Object.freeze({
    sourceRouteId: "vilnius_bus_11",
    shortName: "11",
    longName: "Žvėrynas–Senamiestis–Užupis",
  }),
  trolleybus: Object.freeze({
    sourceRouteId: "vilnius_trol_20",
    shortName: "20",
    longName: "Žirmūnai–Žygimantų–Pylimo g.–Stotis",
  }),
});

/**
 * Authored road decks crossed by the checked-in JUDU route shapes. Keeping
 * this explicit prevents a nearby road running beneath a pedestrian or rail
 * bridge (notably White Bridge) from being lifted onto the wrong level.
 */
export const PUBLIC_TRANSPORT_AUTHORED_BRIDGE_IDS = Object.freeze([
  "gariunai-bridge",
  "gelezinis-vilkas-bridge",
  "green-bridge",
  "lazdynai-bridge",
  "liubartas-bridge",
  "pergale-bridge",
  "pilies-bridge",
  "silo-bridge",
  "valakampiai-bridge",
  "zirmunai-bridge",
] as const);
